<template>
  <div id="life_special_interest">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>

    <usc-intro>
      <p slot="p">As a resident of USC’s special interest housing, you can experience University life with students and faculty who share your special interests, academic achievements or cultural background. Special interest communities feature organized and informal activities and programs.</p>
      <p slot="p">If one or more of these special interest communities seem right for you, please be sure to indicate those communities on the Housing Application. Certain programs, such as Deans’ Halls, have specific eligibility requirements. Programs can fill quickly, so apply early.</p>
      <p slot="p">Applicants to the Arts &amp; Humanities College or International Residential College should list the community you would like as your first choice on your USC Housing Application.</p>
      <usc-cta slot="cta-bottom" text="Complete the supplemental application here" url="http://housing.usc.edu/index.php/how-to-apply-for-residential-colleges-and-special-interest-communities/"></usc-cta>
    </usc-intro>

    <div class="body mb gutter-lg">
      <h2>Business Floor: North Residential College</h2>
      <p>A community geared toward students with majors and career goals related to Business Administration as well as non-business majors who want to explore business applications in their respective fields, Business Floor: North Residential College offers on-site course-scheduling assistance from Marshall advisors, who visit the floor each semester. The Office of Student Services staff also sponsors special activities involving the Los Angeles business community, and faculty and senior administrators often attend weekly Business Floor dinners.</p>
      <p>Residents report that it is easier to work on group assignments in business classes with people who are already their neighbors. Another academic advantage of the Business Floor is the individual and group study rooms, renovations that demonstrate USC Marshall’s commitment to helping students succeed.</p>
    </div>

    <div class="body mb gutter-lg">
      <h2>Arts &amp; Humanities: New Residential College</h2>
      <p>These floors are dedicated to architecture, music and the visual arts. They connect students with the cultural offerings of greater Los Angeles as well as the arts and humanities community at USC.</p>
    </div>

    <usc-videoEmbed :frameTitle="this.video.frameTitle" :caption="this.video.caption" :videoSource="this.video.src"></usc-videoEmbed>

    <div class="body mb gutter-lg">
      <h2>Cinema Floor: New Residential College</h2>
      <p>For students who have an interest in cinema or hope for a career in the industry, the walls of this hallway are decorated with a mural, designed and painted by students, depicting cinema history. Faculty and alumni of the USC School of Cinema Arts are invited to weekly dinners, programs and other activities, including trips to Hollywood and film studios. The floor also sponsors an Ed Wood Film Competition, in which individuals and teams from the entire USC student body are given 24 hours to produce a 10-minute film on a designated topic.</p>
    </div>

    <div class="body mb gutter-lg">
      <h2>University Residential College: Birnkrant</h2>
      <p>Home to more USC campus senators, student government officers, members of honor societies, club leaders, Resident Advisors and award recipients than any other residence hall, this space is reserved for Trustee/Presidential scholars only. Activities include Deans’ Halls Forum, a welcome reception and special dinners.</p>
    </div>

    <div class="body mb gutter-lg">
      <h2>International Residential College: Parkside Apartments</h2>
      <p>The International Residential College is geared toward students interested in intellectual and social exchanges with faculty and students from all over the world, and offers a regular program of guest speakers, social and cultural events and the opportunity to practice language skills.</p>
    </div>

    <div class="body mb gutter-lg">
      <h2>Latino Floors: Fluor Tower</h2>
      <p>These floors promote cultural awareness, community involvement and academic success by facilitating a smooth transition to University life and creating an unforgettable first-year experience for residents from all around the world. The space is open to all students who have an interest in the Latino culture and those who want to experience their first year at USC in a supportive and close-knit multicultural community. For more information, visit: usc.edu/student-affairs/elcentro.</p>
      <p>If you want to live on the Latino Floors, an online application is required. To apply, or for additional information, contact El Centro Chicano at (213) 740-1480 or visit <a class="usc-hyperlink" href="https://elcentro.usc.edu/" target="_blank">USC El Centro Chicano</a>.</p>

      <!-- <usc-cta text="Visit USC El Centro Chicano online" url="https://elcentro.usc.edu/"></usc-cta> -->
    </div>

    <div class="body mb gutter-lg">
      <h2>Muslim Floor: Parkside Apartments</h2>
      <p>Providing a venue for the exploration of the Muslim culture and way of life, this floor is designated as substance-free, with individual kitchens that allow residents to prepare halal food and share Islamic holidays with friends. Through social activities held with USC Muslim organizations, students can learn how Islam relates to other faiths. For more information, contact the <a class="usc-hyperlink" href="http://orl.usc.edu/" target="_blank">USC office of Religious Life</a>.</p>

      <!-- <usc-cta text="For more information, contact the USC Office of Religious Life" url="http://orl.usc.edu/"></usc-cta> -->
    </div>

    <div class="body mb gutter-lg">
      <h2>Quiet Community Floor: Fluor Tower</h2>
      <p>Designed to be a tranquil haven for undergraduate men and women, this space’s residents create a supportive environment designed to help each other focus on academic goals.</p>
    </div>

    <div class="body mb gutter-lg">
      <h2>Rainbow Floor: Century Apartments</h2>
      <p>This floor provides lesbian, gay, bisexual and transgender (LGBTQ+) students and their allies with the opportunity to create a safe and affirming living environment through educational, academic and social programming, in cooperation with the LGBT Resource Center and Student Services’ Residential Education staff. Programs include LGBTQ+ and ally mentoring, social justice initiatives, career and academic support and networking with local Los Angeles resources. For more information, visit the <a class="usc-hyperlink" href="http://lgbtrc.usc.edu/" target="_blank">USC LGBT Resource Center</a> online.</p>

      <!-- <usc-cta text="For more information, visit the LGBT Resource Center online" url="http://lgbtrc.usc.edu/"></usc-cta> -->
    </div>

    <div class="body mb gutter-lg">
      <h2>Shalom Floor: Parkside Apartments</h2>
      <p>Giving students the opportunity to live with Jewish students, explore Jewish culture and connect with the Jewish community, this floor provides individual kitchens in the apartments that make it easier for students who keep kosher to do so. Rabbi Jonathan Klein, director of the USC Hillel Jewish Center, serves as a faculty/staff mentor and brings activities and programs to the floor. For more information, contact the <a class="usc-hyperlink" href="http://orl.usc.edu/" target="_blank">USC office of Religious Life</a></p>

      <!-- <usc-cta text="For more information, contact the USC Office of Religious Life" url="http://orl.usc.edu/"></usc-cta> -->
    </div>

    <div class="body mb gutter-lg">
      <h2>Somerville Place: Fluor Tower</h2>
      <p>Named after John and Vada Somerville, the first two African-American graduates of the USC Dental School, this living space was established to foster understanding of and respect for the African-American culture while developing a sense of family and community. The environment nurtures academic success, leadership, self-direction and personal growth, and its residents strive to achieve spirituality, respect, retention, successful transitions and an understanding and display of positive social values. For more information on Somerville Place and its co-sponsoring office, the Center for Black Cultural and Student Affairs, visit: usc.edu/student-affairs/black_cultural_center.</p>
      <p>Prospective Somerville Place residents must complete a supplemental application. Contact the Center for Black Cultural and Student Affairs at (213) 740-8257, send an <a class="usc-hyperlink" href="mailto:cbcsa@usc.edu">email</a> or <a class="usc-hyperlink" href="https://cbcsa.usc.edu/" target="_blank">visit</a> us online.</p>

      <!-- <usc-cta text="Send an email to the Center for Black Cultural and Student Affairs" url="mailto:cbcsa@usc.edu"></usc-cta>
      <usc-cta text="Visit the Center for Black Cultural and Student Affairs online" url="https://cbcsa.usc.edu/"></usc-cta> -->
    </div>

    <div class="body mb gutter-lg">
      <h2>Substance-Free Floor: New Residential College</h2>
      <p>Here, students are committed to healthy habits and avoid tobacco products, alcohol and other drugs—not just on the floor but also in their daily lives. The floor sponsors activities geared to a substance-free lifestyle, while residents participate in Health Center programs and community service projects, and serve as role models for at-risk youth.</p>
    </div>

    <div class="gutter-md">
      <div class="usc-carousel__header">
        <div class="usc-carousel__title">Life@Marshall</div>
        <div class="usc-carousel__nav">
          <div id="customize-controls" class="usc-carousel__controls">
            <button role="button" aria-label="Previous Image" @click="prevSlide" :disabled="prevDisabled" type="button" class="usc-carousel__button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 13">
                <path fill="#231F20" d="M6 .5c0 .1 0 .2-.1.3L1.3 6.5l4.6 5.7c.1.3.1.6-.1.7-.3.1-.5.1-.7-.1L0 6.5 5.1.2c.2-.2.5-.2.7-.1.1.1.2.3.2.4z"/>
              </svg>
            </button>
            <button role="button" aria-label="Next Image" @click="nextSlide" :disabled="nextDisabled" type="button" class="usc-carousel__button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 13">
                <path fill="#231F20" d="M0 12.5c0-.1 0-.2.1-.3l4.6-5.7L.1.8C0 .6 0 .3.2.1c.3-.1.5-.1.7.1L6 6.5.9 12.8c-.2.2-.5.3-.7.1-.1-.1-.2-.2-.2-.4z"/>
              </svg>
            </button>
          </div>
          <div class="usc-carousel__counter">
            {{this.slideIndex}} / {{this.totalSlides}}
          </div>
        </div>
      </div>
    </div>
    <div class="mb">
      <tiny-slider class="usc-carousel" :mouse-drag="false" :loop="false" items="1" :responsive="true" controlsContainer="#customize-controls">
        <div v-for="(slide, index) in this.slides" class="usc-carousel__slide">
          <!-- <div class="usc-carousel__img" :style="{ backgroundImage: 'url(' + slide.image + ')' }"></div> -->
          <div class="usc-carousel__outer">
            <img class="usc-carousel__inner" :src="slide.image" :alt="slide.alt">
          </div>
        </div>
      </tiny-slider>
    </div>

  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import Cta from '@/components/Cta'
import videoEmbed from '@/components/videoEmbed'
import VueTinySlider from 'vue-tiny-slider'

export default {
  name: 'SpecialInterestCommunities',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-videoEmbed': videoEmbed,
    'usc-cta': Cta,
    'tiny-slider': VueTinySlider
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'Special Interest Communities',
        loResSrc: './static/blur/LifeatMarshall_SpecialInterest.jpg',
        hiResSrc: './static/LifeatMarshall_SpecialInterest.jpg'
      },
      video: {
        caption: 'What does a typical USC Marshall student look like?',
        src: 'https://player.vimeo.com/video/249458067',
        frameTitle: 'Video: a typical USC Marshall student'
      },
      slideIndex: 1,
      vueTinySlider: VueTinySlider,
      prevDisabled: true,
      nextDisabled: false,
      slides: [
        {
          image: '/static/Life_At_Marshall_Special_Interest_Communities_1.jpg',
          alt: 'College student reading her microeconomics book in the laundry room',
          id: 0
        },
        {
          image: '/static/Life_At_Marshall_Special_Interest_Communities_2.jpg',
          alt: 'Students talking to each other',
          id: 1
        },
        {
          image: '/static/Life_At_Marshall_Special_Interest_Communities_3.jpg',
          alt: 'Diverse group of students eating nachos',
          id: 2
        }
      ]
    }
  },
  created () {
    this.$root.setPageHeader('LIFE@MARSHALL', 'Special Interest Communities')
  },
  methods: {
    prevSlide () {
      if (this.slideIndex > 1) {
        this.slideIndex--
      } else {
        this.prevDisabled = true
      }
    },
    nextSlide () {
      if (this.slideIndex < this.totalSlides) {
        this.slideIndex++
      } else {
        this.nextDisabled = true
      }
    }
  },

  computed: {
    totalSlides () {
      return this.slides.length
    }
  }
}
</script>

<style scoped lang="scss">
@import '~tiny-slider/src/tiny-slider';
</style>



// WEBPACK FOOTER //
// src/pages/life-at-marshall/SpecialInterestCommunities.vue